/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import type { ComponentProps, ReactNode } from "react";
import { VisuallyHidden } from "react-aria";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing, useTheme, zIndices } from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { IconButtonProps } from "../../_core/IconButton/IconButton.emotion";
import { IconButton } from "../../_core/IconButton/IconButton.emotion";
import { CloseIcon } from "../../icons/Icon/NavigationIcons";

const closeStyle: Interpolation = {
  marginLeft: "auto",
};
const contentStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  height: "100dvh",
  left: 0,
  overflowY: "auto",
  paddingBottom: spacing.m,
  paddingLeft: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.xxl,
    paddingRight: spacing.xxl,
  },
  paddingRight: spacing.l,
  paddingTop: spacing.m,
  position: "fixed",
  top: 0,
  width: "100vw",
  zIndex: zIndices.modalOverModal,
});

export type FullscreenModalProps = ComponentProps<typeof RadixUIDialog.Root> & {
  closeCSS?: Interpolation;
  closeProps?: Partial<IconButtonProps>;
  contentProps?: ComponentProps<typeof RadixUIDialog.Content>;
  contentCSS?: Interpolation;
  portalProps?: ComponentProps<typeof RadixUIDialog.Portal>;
  trigger?: ReactNode;
  showCloseButton?: boolean;
  removeOverlay?: boolean;
  title?: string;
};

export const FullscreenModal = ({
  children,
  closeCSS = {},
  closeProps = {},
  contentProps,
  contentCSS = {},
  portalProps,
  trigger,
  showCloseButton = true,
  removeOverlay = false,
  title,
  ...props
}: FullscreenModalProps) => {
  const { themeContainerRef } = useTheme();
  const t = useTranslations();

  return (
    <RadixUIDialog.Root {...props}>
      <RadixUIDialog.Trigger asChild>{trigger}</RadixUIDialog.Trigger>
      {!removeOverlay && <RadixUIDialog.Overlay />}
      <RadixUIDialog.Portal
        container={themeContainerRef.current}
        {...portalProps}
      >
        <RadixUIDialog.Content
          css={[contentStyle, contentCSS]}
          {...contentProps}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <VisuallyHidden>
            <RadixUIDialog.Title>
              {title ?? t("general_word_dialog")}
            </RadixUIDialog.Title>
          </VisuallyHidden>
          {showCloseButton && (
            <RadixUIDialog.Close asChild>
              <IconButton
                icon={<CloseIcon />}
                css={[closeStyle, closeCSS]}
                variant="transparentPrimary"
                {...closeProps}
              />
            </RadixUIDialog.Close>
          )}
          {children}
        </RadixUIDialog.Content>
      </RadixUIDialog.Portal>
    </RadixUIDialog.Root>
  );
};
